import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import Logo from "./Logo";
import { navLinks } from "../mock";

export default function Navbar() {
  const [open, setOpen] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-50 transition-[background,backdrop-filter,border] duration-300 ${
        scrolled
          ? "border-b border-slate-200/70 bg-white/80 backdrop-blur-xl"
          : "border-b border-transparent bg-white/0"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" className="flex items-center">
          <Logo />
        </Link>

        {/* desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex">
          {navLinks.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.children && setOpen(item.label)}
              onMouseLeave={() => setOpen(null)}
            >
              {item.children ? (
                <button className="flex items-center gap-1 rounded-md px-3 py-2 text-[12.5px] font-medium uppercase tracking-[0.08em] text-slate-600 transition-colors hover:text-[#1e1b4b]">
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                </button>
              ) : (
                <Link
                  to={item.to || "#"}
                  className="rounded-md px-3 py-2 text-[12.5px] font-medium uppercase tracking-[0.08em] text-slate-600 transition-colors hover:text-[#1e1b4b]"
                >
                  {item.label}
                </Link>
              )}
              {item.children && open === item.label && (
                <div className="absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3">
                  <div className="w-[320px] overflow-hidden rounded-xl border border-slate-200 bg-white p-2 shadow-[0_20px_60px_-15px_rgba(12,27,45,0.18)]">
                    {item.children.map((c) => (
                      <Link
                        key={c.label}
                        to={c.to}
                        className="group flex flex-col gap-0.5 rounded-lg px-3 py-2.5 transition-colors hover:bg-slate-50"
                      >
                        <span className="text-sm font-medium text-[#1e1b4b]">
                          {c.label}
                        </span>
                        {c.desc && (
                          <span className="text-xs text-slate-500">{c.desc}</span>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Link
            to="/login"
            className="text-sm font-medium text-slate-700 hover:text-[#1e1b4b]"
          >
            Login
          </Link>
          <Link
            to="/demo"
            className="group inline-flex items-center gap-1.5 rounded-lg bg-[#1e1b4b] px-4 py-2 text-sm font-medium text-white transition-all hover:bg-[#1e1b4b]/90 hover:shadow-[0_8px_24px_-8px_rgba(12,27,45,0.5)]"
          >
            See Demo
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>

        {/* mobile toggle */}
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="rounded-md p-2 text-slate-700 lg:hidden"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* mobile menu */}
      {mobileOpen && (
        <div className="border-t border-slate-200 bg-white px-5 py-4 lg:hidden">
          <div className="flex flex-col gap-1">
            {navLinks.map((item) => (
              <div key={item.label} className="border-b border-slate-100 py-2 last:border-0">
                <div className="px-1 py-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {item.label}
                </div>
                {item.children ? (
                  <div className="ml-1 flex flex-col">
                    {item.children.map((c) => (
                      <Link
                        key={c.label}
                        to={c.to}
                        onClick={() => setMobileOpen(false)}
                        className="rounded-md px-2 py-1.5 text-sm text-[#1e1b4b] hover:bg-slate-50"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    to={item.to || "#"}
                    onClick={() => setMobileOpen(false)}
                    className="ml-1 rounded-md px-2 py-1.5 text-sm text-[#1e1b4b] hover:bg-slate-50"
                  >
                    Go to {item.label}
                  </Link>
                )}
              </div>
            ))}
            <div className="mt-3 flex gap-2">
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-center text-sm font-medium text-[#1e1b4b]"
              >
                Login
              </Link>
              <Link
                to="/demo"
                onClick={() => setMobileOpen(false)}
                className="flex-1 rounded-lg bg-[#1e1b4b] px-3 py-2 text-center text-sm font-medium text-white"
              >
                See Demo
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
